import { Asset, UInt64 } from "@wharfkit/session";
import TokenDetails from "./TokenDetails";
import { FEE_COLLECTOR, TOKEN_CONTRACT } from "../config";

class AntelopeTokenAPI {
    #session;
    static MAX_TOKEN_SUPPLY = BigInt((2n ** 62n) - 1n); // 2^62 - 1

    constructor(session) {
        this.#session = session;
    };
    
    // Public
    createToken(name, maxSupply, precision, symbol) {
        const asset = this.#assetFromTokenDetails(maxSupply, precision, symbol);

        return this.#createToken(name, asset)
            .then(result => ({trxHash: result.response['transaction_id'], contractAddress: TOKEN_CONTRACT}));
    };

    issueToken(units, token) {
        const precision = token.precision;
        const symbol = token.symbol;
        const asset = this.#assetFromTokenDetails(units, precision, symbol);
        
        return this.#issueToken(asset)
            .then(result => (result.response['transaction_id']));
    };

    async fetchTokenDetails() {
        const accountName = this.#session.actor.toString();
        const tokensResponse = await this.#session.client.v1.chain.get_table_rows({
            code: TOKEN_CONTRACT,
            scope: accountName,
            table: 'issuer',
            limit: 1
        });
        
        if (tokensResponse.rows.length) {
            const symbol = tokensResponse.rows[0]['symbol'];
            const statsReponse = await this.#session.client.v1.chain.get_table_rows({
                code: TOKEN_CONTRACT,
                scope: symbol,
                table: 'stat',
                limit: 1
            });

            const currentSupplyAsset = Asset.from(statsReponse.rows[0]['supply']);
            const maxSupplyAsset = Asset.from(statsReponse.rows[0]['max_supply']);
            const name = statsReponse.rows[0]['name'];
            const currentSupply = BigInt(currentSupplyAsset.units.toString());
            const maxSupply = BigInt(maxSupplyAsset.units.toString());
            const precision = maxSupplyAsset.symbol.precision;
            const contractAddress = TOKEN_CONTRACT;
            
            return new TokenDetails({name, maxSupply, currentSupply, precision, symbol, contractAddress});
        }
    };

    // Private
    #createToken(name, asset) {
        const trx = {
            actions: [{
                account: 'eosio.token',
                name: 'transfer',
                authorization: [this.#session.permissionLevel],
                data: {
                    from: this.#session.actor,
                    to: FEE_COLLECTOR,
                    quantity: '10.0000 TLOS',
                    memo: `For creating a ${asset.symbol.name} token`
                }
            }, {
                account: TOKEN_CONTRACT,
                name: 'create',
                authorization: [this.#session.permissionLevel],
                data: {
                    'issuer': this.#session.actor,
                    'maximum_supply': asset.toString(),
                    'name': name
                }
            }]
        };

        return this.#session.transact(trx);
    };

    #issueToken(asset) {
        const trx = {
            actions: [{
                account: TOKEN_CONTRACT,
                name: 'issue',
                authorization: [this.#session.permissionLevel],
                data: {
                    'to': this.#session.actor,
                    'quantity': asset.toString(),
                    'memo': ''
                }
            }]
        };

        return this.#session.transact(trx);
    };

    #assetFromTokenDetails(amount, precision, symbol) {
        return new Asset(UInt64.from(amount), Asset.Symbol.fromParts(symbol, precision));
    };
}

export default AntelopeTokenAPI;
